import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { useContext, useState } from "react";
import { schoolContext } from "../contexts/SchoolContextProvider";
import { mediaSource, useBackgroundImage } from "../utilities/mediautilities";
import { FaArrowRight, FaPercentage } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { I18N_KEYS } from "../languages/PlaceHolder.js";

const TopStudents = () => {
    const [selectedStudent, setSelectedStudent] = useState(null);
    const { schoolDetails, topperStudents } = useContext(schoolContext);
    const { t } = useTranslation();
    const bgUrl = useBackgroundImage(schoolDetails?.dignataries?.fileUrl)

    return (
        <>
            <section
                className="relative bg-cover bg-center py-20 px-6 mb-10"
                style={{ backgroundImage: `url(${useBackgroundImage(bgUrl)})` }}
            >
                <div className="absolute inset-0 bg-black/50"></div>
                <div className="relative z-10 common-container">
                    <div className="rounded-lg text-left">
                        <h1 className="text-4xl md:text-5xl font-bold mb-4 text-accent">
                            {t(I18N_KEYS.TOPSTUDENTPAGENAME)}
                        </h1>
                        <p className="text-lg md:text-xl text-accent">
                            <Link to="/" className="hover:text-primary">
                                {t(I18N_KEYS.HOMEPAGENAME)}
                            </Link>{" "}
                            &gt; {t(I18N_KEYS.TOPSTUDENTPAGENAME)}
                        </p>
                    </div>
                </div>
            </section>

            <section className="py-12 md:py-16 px-4 md:px-6 bg-gray-50">
                <div className="max-w-7xl mx-auto">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-8">
                        {topperStudents?.map((student, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.5, delay: index * 0.1 }}
                                whileHover={{ y: -5 }}
                                className="group cursor-pointer"
                                onClick={() => setSelectedStudent(student)}
                            >
                                <div className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-xl transition-all duration-300 h-full flex flex-col border border-gray-100">
                                    <div className="relative h-56 bg-gradient-to-br from-primary/10 to-secondary/10 overflow-hidden">
                                        <img
                                            src={mediaSource(student?.payload?.name_ref?.profile_photo?.fileUrl)}
                                            alt={student?.payload?.name_ref?.student_name || "Student"}
                                            onError={(e) => mediaSource(e)}
                                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                                        />
                                        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent"></div>
                                        {student?.payload?.name_ref?.class_ref?.class && (
                                            <div className="absolute top-4 left-4">
                                                <span className="px-3 py-1.5 bg-primary text-accent text-sm font-semibold rounded-full shadow-lg">
                                                    {t(I18N_KEYS.CLASS)} {student?.payload?.name_ref?.class_ref?.class}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                    <div className="p-5 flex-1 flex flex-col">
                                        {student?.payload?.name_ref?.full_name && (
                                            <h3 className="text-lg font-bold text-gray-800 mb-2 group-hover:text-primary transition-colors">
                                                {student?.payload?.full_name}
                                            </h3>
                                        )}
                                        {student?.payload?.percentage && (
                                            <div className="mb-4">
                                                <span className="text-2xl font-black text-secondary flex items-center">
                                                    {student?.payload?.percentage}<FaPercentage />
                                                </span>
                                                <p className="text-sm text-gray-600 mt-1">{t(I18N_KEYS.PERCENTAGE)}</p>
                                            </div>
                                        )}
                                        {student?.payload?.achievement && (
                                            <div className="mb-4">
                                                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">{t(I18N_KEYS.ACHIEVEMENT)}</p>
                                                <p className="text-sm text-primary font-medium line-clamp-2">
                                                    {student?.payload?.achievement}
                                                </p>
                                            </div>
                                        )}
                                        <div className="mt-auto pt-3 border-t border-gray-100">
                                            <span className="text-primary text-sm font-semibold hover:underline flex items-center gap-1">
                                                {t(I18N_KEYS.VIEWPROFILE)}
                                                <span><FaArrowRight /></span>
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>

                {/* Modal for detailed view */}
                <AnimatePresence>
                    {selectedStudent && (
                        <motion.div
                            className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => setSelectedStudent(null)}
                        >
                            <button
                                onClick={() => setSelectedStudent(null)}
                                className="absolute top-4 right-4 text-accent text-3xl font-bold hover:text-primary z-10 w-12 h-12 flex items-center justify-center bg-black/50 rounded-full hover:bg-black/70 transition-colors"
                            >
                                &times;
                            </button>
                            <motion.div
                                className="bg-accent rounded-3xl overflow-hidden shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto"
                                initial={{ scale: 0.8, y: 40 }}
                                animate={{ scale: 1, y: 0 }}
                                exit={{ scale: 0.8, y: 40 }}
                                transition={{ duration: 0.3 }}
                                onClick={(e) => e.stopPropagation()}
                            >
                                <div className="p-8 md:p-12">
                                    <div className="flex justify-center mb-8">
                                        <div className="relative w-48 h-48 md:w-64 md:h-64">
                                            <div className="absolute inset-0 bg-gradient-to-br from-primary via-secondary to-primary rounded-full p-1">
                                                <div className="w-full h-full bg-accent rounded-full overflow-hidden border-4 border-accent">
                                                    <img
                                                        src={mediaSource(selectedStudent?.payload?.name_ref?.profile_photo?.fileUrl)}
                                                        alt={selectedStudent?.payload?.name_ref?.full_name || "Student"}
                                                        className="w-full h-full object-cover"
                                                        onError={(e) => mediaSource(e)}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="text-center">
                                        {selectedStudent?.payload?.name_ref?.full_name && (
                                            <h3 className="text-3xl md:text-4xl font-bold mb-3 text-gray-800">
                                                {selectedStudent?.payload?.name_ref?.full_name}
                                            </h3>
                                        )}
                                        {selectedStudent?.payload?.name_ref?.class_ref?.class && (
                                            <div className="inline-flex items-center gap-2 px-6 py-2 bg-primary/10 rounded-full mb-6">
                                                <span className="text-primary font-semibold text-lg">
                                                    {t(I18N_KEYS.CLASS)} {selectedStudent?.payload?.name_ref?.class_ref?.class}
                                                </span>
                                            </div>
                                        )}
                                        {selectedStudent?.payload?.percentage && (
                                            <div className="mb-6 flex justify-center flex-col">
                                                <span className="text-4xl font-black text-secondary flex items-center justify-center">
                                                    {selectedStudent?.payload?.percentage}<FaPercentage />
                                                </span>
                                                <p className="text-gray-600 mt-2">{t(I18N_KEYS.OVERALLPER)}</p>
                                            </div>
                                        )}
                                        {selectedStudent?.payload?.achievement && (
                                            <div className="bg-secondary/10 rounded-xl p-4 mb-6 border border-secondary/30">
                                                <h4 className="font-semibold text-secondary mb-2">{t(I18N_KEYS.ACHIEVEMENT)}</h4>
                                                <p className="text-secondary font-medium">{selectedStudent?.payload?.achievement}</p>
                                            </div>
                                        )}
                                        {selectedStudent?.payload?.description && (
                                            <div className="bg-primary/5 rounded-xl p-6 mb-6">
                                                <h4 className="font-semibold text-primary mb-3 text-left">{t(I18N_KEYS.ABOUT)}</h4>
                                                <p className="text-gray-700 leading-relaxed text-left"
                                                    dangerouslySetInnerHTML={{
                                                        __html: selectedStudent?.payload?.description
                                                    }}
                                                >
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </section>
        </>
    );
}
export default TopStudents;